import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { CartProvider } from "@/context/CartContext";
import { AuthProvider } from "@/context/AuthContext";
import { StoreStatusProvider, useStoreStatus } from "@/context/StoreStatusContext";
import { GoogleReviewsProvider } from "@/context/GoogleReviewsContext";
import { Toaster } from "@/components/ui/sonner";

import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import CartDrawer from "@/components/site/CartDrawer";
import MobileBottomNav from "@/components/site/MobileBottomNav";
import ScrollToTop from "@/components/site/ScrollToTop";
import StoreClosedBanner from "@/components/site/StoreClosedBanner";
import { ReferralCapture } from "@/components/referrals/ReferralCapture";

import Home from "@/pages/Home";
import Menu from "@/pages/Menu";
import BoxBuilder from "@/pages/BoxBuilder";
import Bulk from "@/pages/Bulk";
import BulkConfirm from "@/pages/BulkConfirm";
import ProductDetail from "@/pages/ProductDetail";
import Reviews from "@/pages/Reviews";
import ReviewForm from "@/pages/ReviewForm";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import OrderConfirmation from "@/pages/OrderConfirmation";
import OrderTracking from "@/pages/OrderTracking";
import About from "@/pages/About";
import Faq from "@/pages/Faq";
import AuthCallback from "@/pages/AuthCallback";
import ComingSoon from "@/pages/ComingSoon";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import MyOrders from "@/pages/MyOrders";
import Account from "@/pages/Account";
import ReferAFriend from "@/pages/ReferAFriend";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminReferrals from "@/pages/admin/AdminReferrals";

function RouterShell() {
  const location = useLocation();
  const { storeMode, loading } = useStoreStatus();
  // Synchronously handle OAuth callback (Emergent Google Auth)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  const isAdmin = location.pathname.startsWith("/admin");
  // Global launch switch — hide the shop until STORE_LIVE_MODE=true. Admin
  // routes stay accessible so the owner can prepare while offline.
  if (!isAdmin && !loading && storeMode && storeMode.live === false) {
    return (
      <div className="grain-overlay min-h-screen bg-ivory">
        <ScrollToTop />
        <ComingSoon />
        <Toaster position="top-center" />
      </div>
    );
  }
  return (
    <div className={isAdmin ? "min-h-screen bg-ivory" : "grain-overlay min-h-screen bg-ivory"}>
      <ScrollToTop />
      <ReferralCapture />
      {!isAdmin && <StoreClosedBanner />}
      {!isAdmin && <Nav />}
      <div className={isAdmin ? "" : "pb-16 md:pb-0"}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/menu" element={<Menu />} />
          <Route path="/box/:size" element={<BoxBuilder />} />
          <Route path="/bulk" element={<Bulk />} />
          <Route path="/bulk/confirm/:token" element={<BulkConfirm />} />
          <Route path="/reviews" element={<Reviews />} />
          <Route path="/review/:token" element={<ReviewForm />} />
          <Route path="/product/:slug" element={<ProductDetail />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/order/:orderNumber" element={<OrderConfirmation />} />
          <Route path="/track" element={<OrderTracking />} />
          <Route path="/track/:orderNumber" element={<OrderTracking />} />
          <Route path="/about" element={<About />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/my-orders" element={<MyOrders />} />
          <Route path="/account" element={<Account />} />
          <Route path="/refer-a-friend" element={<ReferAFriend />} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/referrals" element={<AdminReferrals />} />
        </Routes>
      </div>
      {!isAdmin && <Footer />}
      {!isAdmin && <MobileBottomNav />}
      <CartDrawer />
      <Toaster position="top-center" />
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <StoreStatusProvider>
            <GoogleReviewsProvider>
              <CartProvider>
                <RouterShell />
              </CartProvider>
            </GoogleReviewsProvider>
          </StoreStatusProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
